import { Badge, Button, Tooltip } from '@mui/material';
import usePrintLabelContext from 'common/hooks/usePrintLabelContext';
import useRoles from 'common/hooks/useRoles';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CancelBulkLineItemDialog from './CancelBulkLineItemDialog';

const nonCancellableOrderStates = ['SHIPPED', 'CANCELED', 'IN_SHIPMENT', 'RESHIPMENT'];

const BulkCancelLineItemButton = ({ onSuccess }) => {
  const { labelItemIds } = usePrintLabelContext();
  const { cancelBulkLineItems } = useRoles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [cancellableLineItems, setCancellableLineItems] = useState([]);

  const { pages, currentPage } = useSelector(({ workQueue }) => workQueue.bulkLabels);

  const bulkLabels = useMemo(() => pages[currentPage] || [], [pages, currentPage]);

  useEffect(() => {
    if (!isEmpty(labelItemIds) && !isEmpty(bulkLabels)) {
      const selectedItems = bulkLabels.filter((item) => labelItemIds.includes(item.lineItemId));

      const cancellableItems = selectedItems.filter(
        (item) => !nonCancellableOrderStates.includes(item.processingState)
      );
      setCancellableLineItems(cancellableItems);
    } else {
      setCancellableLineItems([]);
    }
  }, [labelItemIds, bulkLabels]);

  const handleOpenDialog = () => setIsDialogOpen(true);

  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleSuccess = () => {
    handleCloseDialog();
    if (onSuccess) {
      onSuccess();
    }
  };

  if (!cancelBulkLineItems) {
    return null;
  }

  const cancellableItemCount = cancellableLineItems.length;
  const nonCancellableCount = labelItemIds.length - cancellableItemCount;
  const isDisabled = cancellableItemCount === 0;

  let tooltipTitle = '';
  if (nonCancellableCount > 0 && cancellableItemCount > 0) {
    tooltipTitle = `${nonCancellableCount} item${nonCancellableCount > 1 ? 's' : ''} (shipped, cancelled, or in shipment) cannot be cancelled. Only ${cancellableItemCount} item${cancellableItemCount > 1 ? 's' : ''} will be cancelled.`;
  } else if (nonCancellableCount > 0 && cancellableItemCount === 0) {
    tooltipTitle = 'Items that are shipped, cancelled, or in shipment cannot be cancelled.';
  }

  return (
    <>
      <Tooltip title={tooltipTitle} arrow placement='top'>
        <span>
          <Badge color='error' badgeContent={cancellableItemCount} sx={{ mx: 1 }}>
            <Button
              variant='outlined'
              color='error'
              onClick={handleOpenDialog}
              disabled={isDisabled}
              sx={{
                height: 33,
                backgroundColor: (theme) => theme.palette.common.white,
                borderColor: (theme) => theme.palette.error.main,
                color: (theme) => theme.palette.error.main,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.common.white,
                  borderColor: (theme) => theme.palette.error.main,
                },
                '&.Mui-disabled': {
                  backgroundColor: (theme) => theme.palette.action.disabledBackground,
                  borderColor: 'transparent',
                  color: (theme) => theme.palette.action.disabled,
                },
              }}
            >
              Cancel Items
            </Button>
          </Badge>
        </span>
      </Tooltip>

      {isDialogOpen && (
        <CancelBulkLineItemDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          lineItems={cancellableLineItems}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

BulkCancelLineItemButton.propTypes = {
  onSuccess: PropTypes.func,
};

BulkCancelLineItemButton.defaultProps = {
  onSuccess: undefined,
};

export default BulkCancelLineItemButton;
